<script lang="ts" setup>
import { computed, nextTick, shallowRef, ref, watch, type PropType } from "vue";
import { useI18n } from "vue-i18n";
import { refDebounced, useMediaQuery } from "@vueuse/core";

import { LoadingSpinner, Search, Tooltip } from "@/components";
import {
  useCrmContactsQuery,
  useCrmContactsWithoutSearchQuery,
  CrmContact,
  useDocumentSpaceConfigsQuery,
} from "@/graphql/operations";
import ExpandedRow from "./ExpandedRow.vue";

const { t } = useI18n({});

const props = defineProps({
  multiple: { type: Boolean, default: false },
  selectedContacts: {
    type: Array as PropType<
      Array<{ id: string; firstName?: string | null; lastName?: string | null }>
    >,
    default: () => [],
  },
  spaceMode: {
    type: Boolean,
    default: true,
  },
  inheritClassicTheme: {
    type: Boolean,
    default: false,
  },
});
const isMultiple = computed(() => props.multiple && !props.spaceMode);
const isSelected = (id: string) =>
  isMultiple.value
    ? props.selectedContacts.some((contact) => contact.id === id)
    : selectedContact.value === id;
const removeContact = (id: string) =>
  emit(
    "selection-change",
    props.selectedContacts.filter((contact) => contact.id !== id),
  );
const isDarkMode = useMediaQuery("(prefers-color-scheme: dark)");

const emit = defineEmits(["submit", "change", "close", "selection-change"]);

const highlightedIdx = ref(-1);

const { data: configsData } = useDocumentSpaceConfigsQuery();
const numSpaceConfigs = computed(
  () => configsData.value?.documentSpaceConfigs?.totalCount || 1,
);

const search = shallowRef<string>("");
const currentSearch = refDebounced(search, 500, { maxWait: 2250 });
const limit = ref<number>(100);
const offset = ref<number>(0);

const { data: dataWithSearch, fetching: fetchingWithSearch } =
  useCrmContactsQuery({
    variables: computed(() => ({
      search: currentSearch.value,
      limit: limit.value,
      offset: offset.value,
    })),
    requestPolicy: "cache-and-network",
    pause: computed(() => currentSearch.value?.trim().length === 0),
  });

const { data: dataWithoutSearch, fetching: fetchingWithoutSearch } =
  useCrmContactsWithoutSearchQuery({
    variables: computed(() => ({
      limit: 30,
      offset: offset.value,
    })),
    requestPolicy: "cache-and-network",
    pause: computed(() => currentSearch.value?.trim().length > 0),
  });

const activeFetching = computed(() => {
  if (!isMultiple.value && (!props.inheritClassicTheme || !isDarkMode.value)) {
    return fetchingWithSearch.value;
  }

  return currentSearch.value?.trim().length === 0
    ? fetchingWithoutSearch.value
    : fetchingWithSearch.value;
});

const hasNextPage = computed(() =>
  currentSearch.value?.trim().length === 0
    ? dataWithoutSearch.value?.crmContacts?.pageInfo?.hasNextPage
    : dataWithSearch.value?.rankedSearchCrmContactsByName?.pageInfo
        ?.hasNextPage,
);
const hasPreviousPage = computed(() =>
  currentSearch.value?.trim().length === 0
    ? dataWithoutSearch.value?.crmContacts?.pageInfo?.hasPreviousPage
    : dataWithSearch.value?.rankedSearchCrmContactsByName?.pageInfo
        ?.hasPreviousPage,
);

const filterReset = ref<boolean>(false);

const setFilter = (filter: string) => {
  if (typeof filter !== "string") {
    return;
  }
  search.value = filter;
};

const selectedContact = ref<string | null>(null);

const contacts = computed(() =>
  currentSearch.value?.trim().length === 0
    ? dataWithoutSearch.value?.crmContacts?.nodes
    : dataWithSearch.value?.rankedSearchCrmContactsByName?.nodes,
);

watch(contacts, () => (highlightedIdx.value = -1));

const contactIsDisabled = (contact: {
  documentSpaceCrmMappings: { totalCount: number };
}) =>
  props.spaceMode &&
  contact.documentSpaceCrmMappings.totalCount >= numSpaceConfigs.value;

const selectableContactIndexes = computed(() =>
  (contacts.value ?? []).flatMap((contact, index) =>
    contact && !contactIsDisabled(contact) ? [index] : [],
  ),
);

const changeSelection = (id: string) => {
  const contact = contacts.value!.find((c) => c!.id === id);
  if (!contact) {
    return;
  }

  if (
    props.spaceMode &&
    contact.documentSpaceCrmMappings.totalCount >= numSpaceConfigs.value
  ) {
    console.log("display warning");
    return;
  }

  if (isMultiple.value) {
    if (isSelected(id)) removeContact(id);
    else
      emit("selection-change", [
        ...props.selectedContacts,
        {
          id: contact.id,
          firstName: contact.firstName,
          lastName: contact.lastName,
        },
      ]);
    return;
  }

  selectedContact.value = id;
  expandedRows.value = [id];

  const nameParts = [contact.lastName];
  if (contact.firstName) {
    nameParts.push(contact.firstName);
  }

  const name = nameParts.join(", ");
  emit("change", contact, name);
};

const submit = async () => {
  if (!selectedContact.value) {
    return;
  }

  emit("submit");
};

const formatName = (contact: {
  firstName?: string | null;
  lastName?: string | null;
}) => {
  const name = [contact.lastName];
  if (contact.firstName) {
    name.push(contact.firstName);
  }

  return name.join(", ");
};

const listElem = ref<HTMLElement>();

const rowAt = (index: number) =>
  listElem.value?.children[index]?.querySelector<HTMLElement>(".row");

const moveHighlight = async (direction: 1 | -1) => {
  const selectable = selectableContactIndexes.value;
  if (selectable.length === 0) {
    highlightedIdx.value = -1;
    return;
  }

  const currentPosition = selectable.indexOf(highlightedIdx.value);
  const nextPosition =
    currentPosition === -1
      ? direction === 1
        ? 0
        : selectable.length - 1
      : Math.min(
          Math.max(currentPosition + direction, 0),
          selectable.length - 1,
        );

  highlightedIdx.value = selectable[nextPosition];
  await nextTick();
  rowAt(highlightedIdx.value)?.focus();
};

const handleEnter = () => {
  const contact = contacts.value?.[highlightedIdx.value];
  if (!contact || contactIsDisabled(contact)) {
    return;
  }

  changeSelection(contact.id);
  if (!isMultiple.value) submit();
};

const handleKeyDown = (ev: KeyboardEvent) => {
  if (
    isMultiple.value &&
    !(ev.target as HTMLElement).closest(".navigatable_list, .search")
  ) {
    // Buttons/chips own their keyboard behavior. Search still supports arrows.
    if (
      (ev.target as HTMLElement).tagName !== "INPUT" ||
      (ev.target as HTMLInputElement).type === "checkbox"
    )
      return;
  }
  if (ev.code === "ArrowDown") {
    ev.preventDefault();
    void moveHighlight(1);
  } else if (ev.code === "ArrowUp") {
    ev.preventDefault();
    void moveHighlight(-1);
  } else if (
    ev.code === "Enter" ||
    (isMultiple.value &&
      ev.code === "Space" &&
      (ev.target as HTMLElement).closest(".row"))
  ) {
    if (highlightedIdx.value === -1) {
      return;
    }
    ev.preventDefault();
    handleEnter();
  }
};

const goToPreviousPage = () => {
  if (hasPreviousPage.value)
    offset.value = Math.max(0, offset.value - pageLimit.value);
};

const goToNextPage = () => {
  if (hasNextPage.value) offset.value += pageLimit.value;
};

const pageLimit = computed(() =>
  isMultiple.value && !currentSearch.value.trim() ? 30 : limit.value,
);
watch(currentSearch, () => {
  if (isMultiple.value) offset.value = 0;
});

const expandedRows = ref<Array<string>>([]);
const toggleRow = (contactId: string) => {
  if (expandedRows.value.includes(contactId)) {
    expandedRows.value.splice(expandedRows.value.indexOf(contactId), 1);
  } else {
    expandedRows.value.push(contactId);
  }
};
</script>

<template>
  <div @keydown="handleKeyDown" class="body_container">
    <div>
      <Search
        :placeholder="$t('crmContactSearch.search.placeholder')"
        :resetValue="filterReset"
        @change="setFilter"
        :initialFocus="true"
        :inherit-classic-theme="props.inheritClassicTheme"
      />
    </div>

    <div
      v-if="isMultiple && selectedContacts.length"
      class="selected_contacts"
      data-test="selected-contacts"
    >
      <button
        v-for="contact in selectedContacts"
        :key="contact.id"
        type="button"
        class="contact_chip"
        :aria-label="
          $t('documentHistory.hcps.remove', { name: formatName(contact) })
        "
        @click="removeContact(contact.id)"
      >
        {{ formatName(contact) }} <span aria-hidden="true">×</span>
      </button>
    </div>
    <div v-if="activeFetching" class="loading_status">
      <LoadingSpinner :color="'var(--classic-text, black)'" />
    </div>
    <div v-else-if="contacts && contacts.length > 0">
      <div class="controls">
        <div class="control">
          <tooltip>
            <template v-slot>
              <span
                :class="{
                  'material-symbols-outlined': true,
                  disabled: !hasPreviousPage,
                }"
                @click="goToPreviousPage"
              >
                chevron_left
              </span>
            </template>
            <template #tooltip>{{
              $t("crmContacts.pagination.previous")
            }}</template>
          </tooltip>
          <tooltip>
            <template v-slot>
              <span
                :class="{
                  'material-symbols-outlined': true,
                  disabled: !hasNextPage,
                }"
                @click="goToNextPage"
              >
                chevron_right
              </span>
            </template>
            <template #tooltip>{{
              $t("crmContacts.pagination.next")
            }}</template>
          </tooltip>
        </div>
      </div>
      <div class="clickable navigatable_list" ref="listElem">
        <div v-for="(contact, i) in contacts" :key="contact!.id">
          <div
            :class="{
              row: true,
              clickable_row: true,
              highlighted: i === highlightedIdx || isSelected(contact!.id),
              disabled:
                spaceMode &&
                contact!.documentSpaceCrmMappings.totalCount >= numSpaceConfigs,
            }"
            @focusin="highlightedIdx = i"
            @click.stop="changeSelection(contact!.id)"
            :tabindex="i === highlightedIdx ? 0 : -1"
            :data-test="`crm-contact-${contact!.id}`"
          >
            <div>
              <div class="checkbox_container">
                <input
                  :type="isMultiple ? 'checkbox' : 'radio'"
                  :tabindex="isMultiple ? -1 : undefined"
                  :aria-label="formatName(contact!)"
                  :disabled="
                    spaceMode &&
                    contact!.documentSpaceCrmMappings.totalCount >=
                      numSpaceConfigs
                  "
                  :id="contact!.id"
                  :value="contact!.id"
                  :checked="isSelected(contact!.id)"
                  :class="{
                    disabled:
                      spaceMode &&
                      contact!.documentSpaceCrmMappings.totalCount >=
                        numSpaceConfigs,
                  }"
                />
              </div>
            </div>

            <div class="main_column">
              <Tooltip class="left">
                <template v-slot>
                  <div>
                    <b>
                      {{ formatName(contact! as CrmContact) }}
                    </b>
                  </div>
                  <div class="info">
                    {{ contact!.email }}
                  </div>
                  <div class="info">
                    {{ contact!.organization }}
                    <template
                      v-if="contact!.organization && contact!.territory"
                    >
                      &mdash;
                    </template>
                    {{ contact!.territory }}
                  </div>
                </template>
                <template
                  #tooltip
                  v-if="
                    spaceMode &&
                    contact!.documentSpaceCrmMappings.totalCount >=
                      numSpaceConfigs
                  "
                >
                  {{ $t("crmContacts.warning.spaces") }}
                </template>
              </Tooltip>
            </div>
            <div class="col" style="width: 15%">
              {{
                contact!.crmContactPhaseMappingsByContactId?.nodes[0]?.phase
                  ?.name
              }}
            </div>
            <div
              v-if="
                spaceMode && contact!.documentSpaceCrmMappings.totalCount > 0
              "
              class="right"
            >
              <router-link
                @click="emit('close')"
                v-if="numSpaceConfigs === 1"
                class="link"
                :to="{
                  name: 'DocumentSpace',
                  params: {
                    id: contact?.documentSpaceCrmMappings.nodes[0]
                      ?.documentSpaceId,
                  },
                }"
              >
                {{ $t("crmContacts.openSpace") }}
              </router-link>
              <div v-else class="container">
                <span class="text">
                  {{
                    $t("crmContacts.info.spaces.totalCount", {
                      count: contact!.documentSpaceCrmMappings.totalCount,
                    })
                  }}
                </span>
                <span
                  class="clickable material-symbols-outlined"
                  @click.stop="toggleRow(contact!.id)"
                >
                  {{
                    expandedRows.includes(contact!.id)
                      ? "keyboard_arrow_down"
                      : "chevron_right"
                  }}
                </span>
              </div>
            </div>
          </div>
          <ExpandedRow
            v-if="
              spaceMode &&
              expandedRows.includes(contact!.id) &&
              contact!.documentSpaceCrmMappings.totalCount > 0
            "
            :contactId="contact!.id"
            :class="{ selected: selectedContact === contact!.id }"
            @close="emit('close')"
          />
        </div>
      </div>
      <div class="controls">
        <div class="control">
          <tooltip>
            <template v-slot>
              <span
                :class="{
                  'material-symbols-outlined': true,
                  disabled: !hasPreviousPage,
                }"
                @click="goToPreviousPage"
              >
                chevron_left
              </span>
            </template>
            <template #tooltip>{{
              $t("crmContacts.pagination.previous")
            }}</template>
          </tooltip>
          <tooltip>
            <template v-slot>
              <span
                :class="{
                  'material-symbols-outlined': true,
                  disabled: !hasNextPage,
                }"
                @click="goToNextPage"
              >
                chevron_right
              </span>
            </template>
            <template #tooltip>{{
              $t("crmContacts.pagination.next")
            }}</template>
          </tooltip>
        </div>
      </div>
    </div>
    <div v-else class="info">
      {{ $t("crmContactSearch.emptyResult") }}
    </div>
  </div>
</template>

<style scoped lang="scss">
.selected_contacts {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4em;
  margin: 0.8em 0;
}
.contact_chip {
  font: inherit;
  color: inherit;
  background: var(--classic-surface-hover, #f4f4f4);
  border: 1px solid var(--classic-border-subtle, #ddd);
  border-radius: 1em;
  padding: 0.25em 0.7em;
  cursor: pointer;
}
input[type="checkbox"] {
  accent-color: var(--classic-accent, #34b5ba);
  width: 14px;
  height: 14px;
}
.row:focus-visible,
.contact_chip:focus-visible {
  outline: 2px solid var(--classic-focus, #34b5ba);
  outline-offset: 2px;
}
$checkbox-width: 40px;
$color-secondary: #34b5ba;

.navigatable_list {
  width: 100%;
  font-size: 0.9rem;
}

.navigatable_list .row {
  display: flex;
  align-items: center;
  border-top: 1px solid var(--classic-border-subtle, #f4f4f4);
  padding: 0.1em 0;
}

.main_column {
  padding: 0.5em 0;
}

.navigatable_list.clickable .row.highlighted,
.navigatable_list.clickable .row:hover {
  cursor: pointer;
  background-color: var(--classic-surface-hover, #fafafa);
}

.expanded_row.selected {
  background-color: var(--classic-surface-hover, #fafafa);
}

.navigatable_list.clickable .row.highlighted.disabled,
.navigatable_list.clickable .row.disabled:hover {
  cursor: initial;
}

.navigatable_list .row > div {
  display: inline-block;
  white-space: nowrap;
  text-overflow: ellipsis;
  margin-right: 2em;
}

.navigatable_list .row > div.right {
  margin-right: 1em;
}

.navigatable_list .row > div:first-child {
  width: $checkbox-width;
  margin-right: 0;
}

.navigatable_list .row > div:nth-child(3) {
  margin-left: auto;
}

.row b {
  font-weight: 500;
  line-height: 1.5;
}

.row .info {
  font-size: 0.9em;
}

input[type="radio"] {
  appearance: none;
  width: 13px;
  height: 13px;
  border: 1px solid var(--classic-border-strong, #555555);
  border-radius: 50%;
  background-clip: content-box;
  padding: 2px;
}

input[type="radio"].disabled {
  background-color: var(--classic-control-disabled, #dadada);
  pointer-events: none;
}

input[type="radio"].disabled:hover {
  cursor: default;
  pointer-events: none;
}

input[type="radio"]:checked {
  background-color: var(--classic-accent, $color-secondary);
}

@media (prefers-color-scheme: dark) {
  :global(.classic-theme)
    input[type="radio"]:focus-visible:not(:global(.classic-theme-light-only *)),
  :global(.classic-theme)
    .navigatable_list
    .row:focus-visible:not(:global(.classic-theme-light-only *)) {
    outline: 2px solid var(--classic-focus);
    outline-offset: 2px;
  }
}

.checkbox_container {
  vertical-align: middle;
  padding: 0.8em 0.8em 0.8em 0.5em;
}

.modal .footer .buttons {
  left: auto;
}

.checkbox_with_label {
  position: absolute;
  left: 22px;
  top: 50%;
  transform: translateY(-50%);
}

.checkbox_with_label .checkbox_container {
  padding: 0;
  margin-right: 0.5em;
}

.body_container {
  height: 100%;
}
.loading_status {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 75%;
}

.controls {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 0.5em 0;
}

.control span {
  cursor: pointer;
  font-size: 1.1rem;
  border-radius: 50%;
  padding: 0.58em;
  line-height: 1;
}

.control span:hover {
  background-color: var(--classic-surface-hover, #f4f4f4);
}

.control span.disabled {
  pointer-events: none;
  cursor: initial;
  opacity: 0.3;
}

.col {
  overflow: hidden;
  text-overflow: ellipsis;
}

.link {
  color: inherit;
}

.right .container {
  display: flex;
  align-items: center;
}

.right .container .text {
  width: 110px;
}
</style>
